import * as React from 'react';
import styled, { css } from 'styled-components';
import { Button, type ButtonProps } from '../../primitives/Button/Button';
import type { ButtonSize } from '../../primitives/Button/buttonTypes';
import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';

const compactButtonStyles = css`
  & > button {
    min-height: unset;
    height: auto;
    padding: ${rem(4)} ${rem(8)};
    font-size: ${rem(14)};
    line-height: ${rem(16)};
    gap: ${rem(4)};
    white-space: nowrap;

    .material-symbols-outlined {
      font-size: ${rem(16)};
      width: ${rem(16)};
      height: ${rem(16)};
    }
  }
`;

const StyledButtonGroup = styled.div<{ $compact?: boolean }>`
  display: inline-flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: stretch;
  max-width: 100%;

  ${({ $compact }) => ($compact ? compactButtonStyles : undefined)}

  & > button {
    position: relative;
    z-index: 0;
    transition:
      background 0.2s ease-in-out,
      color 0.2s ease-in-out,
      border-color 0.2s ease-in-out;

    &:not(:first-child) {
      margin-left: -${rem(1)};
    }

    &:not(:last-child) {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }

    &:not(:first-child) {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }

    &:not(:last-child)::after {
      content: '';
      position: absolute;
      top: -${rem(1)};
      right: -${rem(1)};
      height: calc(100% + ${rem(2)});
      width: ${rem(1)};
      background: var(--ui-border);
      pointer-events: none;
      z-index: 1;
    }

    &:hover {
      z-index: 2;
    }
  }
`;

export interface ButtonGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
  /** Legacy BtnGroup-style tight padding (tax rule builder toolbar). */
  compact?: boolean;
  /** Applied to each item unless overridden on the item. */
  size?: ButtonSize;
  children: React.ReactNode;
}

const ButtonGroupSizeContext = React.createContext<ButtonSize>('sm');

export function ButtonGroup({
  semanticId,
  compact = false,
  size = compact ? 'sm' : 'md',
  children,
  className,
  ...rest
}: ButtonGroupProps) {
  return (
    <ButtonGroupSizeContext.Provider value={size}>
      <StyledButtonGroup
        className={className}
        role='group'
        $compact={compact}
        {...getSemanticAttributes(semanticId)}
        {...rest}
      >
        {children}
      </StyledButtonGroup>
    </ButtonGroupSizeContext.Provider>
  );
}

export type ButtonGroupItemProps = Omit<ButtonProps, 'asChild' | 'children'>;

export function ButtonGroupItem({ size: sizeProp, ...props }: ButtonGroupItemProps) {
  const sizeFromGroup = React.useContext(ButtonGroupSizeContext);
  return <Button type='button' size={sizeProp ?? sizeFromGroup} {...props} />;
}

ButtonGroup.displayName = 'ButtonGroup';
ButtonGroupItem.displayName = 'ButtonGroupItem';
